import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartCountProvider } from './context/CartCountContext';
import { SnackbarProvider } from './context/SnackbarContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import ProductDetails from './pages/ProductDetails';
import Cart from './pages/Cart';
import Wallet from './pages/Wallet';
import AdminDashboard from './pages/AdminDashboard';
import AdminProducts from './pages/AdminProducts';
import AdminOrders from './pages/AdminOrders';
import AdminChats from './pages/AdminChats';
import AdminMembershipPlans from './pages/AdminMembershipPlans';
import AdminWallets from './pages/AdminWallets';
import OrderHistory from './pages/OrderHistory';
import About from './pages/About';
import Legal from './pages/Legal';
import Privacy from './pages/Privacy';
import Messages from './pages/Messages';
import MembershipPlans from './pages/MembershipPlans';
import Welcome from './pages/Welcome';
import VerifyEmail from './pages/VerifyEmail';
import SocialAuthCallback from './pages/SocialAuthCallback';
import Navbar from './components/Navbar';
import ChatWidget from './components/ChatWidget';
import AdminLayout from './components/AdminLayout';
import UserLayout from './components/UserLayout';
import { trackEvent } from './services/telemetry';
import './App.css';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  return user ? <>{children}</> : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  if (!user.isAdmin) {
    return <Navigate to="/dashboard" />;
  }
  return <>{children}</>;
};

const LandingRoute: React.FC = () => {
  return <Landing />;
};

const TelemetryTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    trackEvent({
      event: 'page_view',
      properties: { path: location.pathname, search: location.search },
      pageUrl: window.location.href,
    });
  }, [location.pathname, location.search]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <CartCountProvider>
        <SnackbarProvider>
          <Router>
            <div className="App">
              <TelemetryTracker />
              <Navbar />
              <ChatWidget />
              <Routes>
            <Route path="/" element={<LandingRoute />} />
            <Route path="/about" element={<About />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/plans" element={<MembershipPlans />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/social" element={<SocialAuthCallback />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <UserLayout>
                    <Dashboard />
                  </UserLayout>
                </PrivateRoute>
              }
            />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetails />} />
            <Route path="/cart" element={<Cart />} />
            <Route
              path="/wallet"
              element={
                <PrivateRoute>
                  <UserLayout>
                    <Wallet />
                  </UserLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <PrivateRoute>
                  <UserLayout>
                    <OrderHistory />
                  </UserLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <PrivateRoute>
                  <UserLayout>
                    <Messages />
                  </UserLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminDashboard />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminProducts />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminOrders />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/wallets"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminWallets />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/membership-plans"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminMembershipPlans />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/chats"
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminChats />
                  </AdminLayout>
                </AdminRoute>
              }
            />
              </Routes>
            </div>
          </Router>
        </SnackbarProvider>
      </CartCountProvider>
    </AuthProvider>
  );
}

export default App;

